import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import * as Turf from "@turf/helpers";
import { geojsonType, getGeom } from "@turf/invariant";
import type {
  FeatureCollection,
  GeoJsonObject,
  Geometry,
  MultiPolygon,
  Polygon,
  Position,
} from "geojson";
import * as R from "src/types/result";

export function safeParse(value: unknown): R.Result<GeoJsonObject> {
  try {
    geojsonType(value, "FeatureCollection", "geojson");
  } catch (e) {
    if (e instanceof Error) {
      return R.failure(e.message);
    }

    return R.failure("Unexpected error in parsing GeoJSON");
  }

  return R.success(value as GeoJsonObject);
}

export function isFeatureCollection(
  geoJSON: GeoJsonObject
): geoJSON is FeatureCollection {
  return geoJSON.type === "FeatureCollection";
}

export function safeGetGeometry(
  geoJSON: GeoJsonObject,
  geoJsonProperty: { key: string; value: string }
): R.Result<Geometry> {
  if (!isFeatureCollection(geoJSON)) {
    return R.failure(
      `Unsupported GeoJSON type: ${geoJSON.type}. We currently only support FeatureCollection`
    );
  }

  const feature = geoJSON.features.find((f) => {
    const propertyValue = f.properties?.[geoJsonProperty.key];
    return propertyValue === geoJsonProperty.value;
  });

  if (feature === undefined) {
    return R.failure(`Unable to get geometry for ${geoJsonProperty.value}`);
  }

  return R.success(getGeom(feature));
}

export function isInside(
  geometry: Geometry,
  location: Position
): R.Result<boolean> {
  const { type } = geometry;

  switch (type) {
    case "Polygon":
      return geometryToPolygon(geometry as Polygon).map((polygon) => {
        return booleanPointInPolygon(location, polygon);
      });
    case "MultiPolygon":
      return geometryToMultiPolygon(geometry as MultiPolygon).map(
        (multiPolygon) => {
          return booleanPointInPolygon(location, multiPolygon);
        }
      );
    default:
      return R.failure(`Unsupported geometry type: ${geometry.type}`);
  }
}

export function isOutside(
  geometry: Geometry,
  location: Position
): R.Result<boolean> {
  return isInside(geometry, location).map((value) => !value);
}

function geometryToPolygon(geometry: Polygon): R.Result<Polygon> {
  const coordinates = geometry.coordinates;
  return coordinatesToPaths(coordinates).map((paths) => {
    return Turf.polygon(paths).geometry;
  });
}

function geometryToMultiPolygon(
  geometry: MultiPolygon
): R.Result<MultiPolygon> {
  const coordinates = geometry.coordinates;
  return multiPolygonCoordinatesToPaths(coordinates).map((paths) => {
    return Turf.multiPolygon(paths).geometry;
  });
}

function multiPolygonCoordinatesToPaths(
  coordinates: Position[][][]
): R.Result<Position[][][]> {
  return R.join(coordinates.map(coordinatesToPaths));
}

function coordinatesToPaths(coordinates: Position[][]): R.Result<Position[][]> {
  return R.join(coordinates.map(positionsToPath));
}

function positionsToPath(positions: Position[]): R.Result<Position[]> {
  return R.join(positions.map(positionToPath));
}

export function positionToPath(position: Position): R.Result<[number, number]> {
  const [lng, lat] = position;
  if (lng === undefined || lat === undefined) {
    return R.failure(`Invalid position format: ${position}`);
  }

  return R.success([lng, lat]);
}
